import React from "react";
import { Flex, Button, Image } from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { AppDispatch, setPageLoading, useErrorHandler } from "../index";
// @ts-ignore
import { Mixpanel } from "../../Mixpanel";
import GoogleIcon from "../../img/google.svg";
import MicrosoftIcon from "../../img/microsoft.svg";

interface SocialLoginButtonsProps {
	action: "login" | "register";
	isMobile?: boolean;
}

export const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = ({
	action,
	isMobile = false,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const handleError = useErrorHandler();

	const initiateOAuth = async (provider: "google" | "azure") => {
		try {
			dispatch(
				setPageLoading({
					loading: true,
					loadingText: `Redirecting to ${provider === "google" ? "Google" : "Microsoft"}...`,
				}),
			);

			const response = await fetch(
				`/api/method/ace_be_app.api.auth.get_oauth2_authorize_url?provider=${provider}&redirect_to=${window.location.origin}/fileupload`,
			);

			if (!response.ok) throw new Error(`HTTP ${response.status}`);

			const data = await response.json();
			if (!data?.message) throw new Error("Invalid OAuth response");

			const eventPrefix =
				action === "login"
					? `${provider} Login Initiation`
					: `${provider} Register Initiation`;

			(window as any).dataLayer?.push({
				event: `${eventPrefix} Success`,
				success: true,
			});

			Mixpanel.track(`${eventPrefix} Success`, {
				success: true,
			});

			localStorage.setItem("auth_provider", provider);
			localStorage.setItem("auth_action", action);

			window.location.assign(data.message);
		} catch (error) {
			const eventPrefix =
				action === "login"
					? `${provider} Login Initiation`
					: `${provider} Register Initiation`;
			(window as any).dataLayer?.push({
				event: `${eventPrefix} Failed`,
				success: false,
			});

			Mixpanel.track(`${eventPrefix} Failed`, {
				success: false,
			});
			handleError(error);
		} finally {
			dispatch(setPageLoading({ loading: false, loadingText: "" }));
		}
	};

	const buttonTextGoogle =
		action === "login" ? "Sign in with Google" : "Sign up with Google";
	const buttonTextMicrosoft =
		action === "login" ? "Sign in with Microsoft" : "Sign up with Microsoft";
	const googleId =
		action === "login" ? "ace_google_login_btn" : "ace_google_register_btn";
	const microsoftId =
		action === "login"
			? "ace_microsoft_login_btn"
			: "ace_microsoft_register_btn";
	const googleClass =
		action === "login" ? "aceGoogleLoginBtn" : "aceGoogleRegisterBtn";
	const microsoftClass =
		action === "login" ? "aceMicrosoftLoginBtn" : "aceMicrosoftRegisterBtn";

	if (isMobile) {
		return (
			<Flex
				alignItems="center"
				justifyContent="center"
				gap="1rem"
				width="100%"
				flexWrap="wrap"
			>
				<Button
					id={googleId}
					className={googleClass}
					variant="outline"
					size="sm"
					w="100%"
					bg="#fff"
					color="#3C4043"
					border="1px solid #D1D5DB"
					display="flex"
					flexDirection={{ base: "column", sm: "row" }}
					alignItems="center"
					justifyContent="center"
					gap="0.5rem"
					h="auto"
					py="0.75rem"
					_hover={{
						transform: "translateY(-1px)",
						boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
					}}
					transition="all 0.2s ease"
					leftIcon={<Image src={GoogleIcon} alt="Google" />}
					fontSize=".875rem"
					fontWeight={500}
					onClick={() => initiateOAuth("google")}
				>
					{buttonTextGoogle}
				</Button>
				<Button
					id={microsoftId}
					className={microsoftClass}
					variant="outline"
					size="sm"
					w="100%"
					bg="#fff"
					color="#5E5E5E"
					border="1px solid #D1D5DB"
					display="flex"
					flexDirection={{ base: "column", sm: "row" }}
					alignItems="center"
					justifyContent="center"
					gap="0.5rem"
					h="auto"
					py="0.75rem"
					_hover={{
						transform: "translateY(-1px)",
						boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
					}}
					transition="all 0.2s ease"
					leftIcon={<Image src={MicrosoftIcon} alt="Microsoft" />}
					fontSize=".875rem"
					fontWeight={500}
					onClick={() => initiateOAuth("azure")}
				>
					{buttonTextMicrosoft}
				</Button>
			</Flex>
		);
	}

	return (
		<Flex
			flexDirection={{ md: "column", lg: "row" }}
			alignItems="center"
			justifyContent="center"
			gap="1rem"
			width="100%"
			flexWrap="wrap"
		>
			<Button
				id={googleId}
				className={googleClass}
				variant="outline"
				size="sm"
				flex={{ base: "100%", lg: 1 }}
				width={{ md: "100%", lg: "auto" }}
				py="2"
				h="auto"
				whiteSpace="normal"
				bg="#F9F9F9"
				color="#5E6278"
				border="1px solid #EFF2F5"
				_hover={{
					transform: "translateY(-1px)",
					boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
				}}
				leftIcon={<Image src={GoogleIcon} alt="Google" />}
				fontSize=".875rem"
				fontWeight={500}
				onClick={() => initiateOAuth("google")}
			>
				{buttonTextGoogle}
			</Button>
			<Button
				id={microsoftId}
				className={microsoftClass}
				variant="outline"
				size="sm"
				flex={{ base: "100%", lg: 1 }}
				width={{ md: "100%", lg: "auto" }}
				whiteSpace="normal"
				py="2"
				h="auto"
				bg="#F9F9F9"
				color="#5E6278"
				border="1px solid #EFF2F5"
				_hover={{
					transform: "translateY(-1px)",
					boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
				}}
				leftIcon={<Image src={MicrosoftIcon} alt="Microsoft" />}
				fontSize=".875rem"
				fontWeight={500}
				onClick={() => initiateOAuth("azure")}
				objectFit="contain"
			>
				{buttonTextMicrosoft}
			</Button>
		</Flex>
	);
};
